import { useEffect, useState, useRef, type FC } from "react";

interface CSSBackgroundProps {
  colorStops: Array<{ color: string | number[]; progress: number }>;
}

export const CSSBackground: FC<CSSBackgroundProps> = ({ colorStops }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      // calculate scroll progress (0 to 1)
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollHeight > 0 ? Math.min(window.scrollY / scrollHeight, 1) : 0;

      // use RAF for smooth updates
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      animationRef.current = requestAnimationFrame(() => {
        setScrollProgress(progress);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // find the current color based on scroll progress
  const getCurrentColor = () => {
    // find the two stops that bracket the current progress
    let lowerIdx = 0;
    for (let i = 0; i < colorStops.length - 1; i++) {
      if (scrollProgress >= colorStops[i].progress && scrollProgress <= colorStops[i + 1].progress) {
        lowerIdx = i;
        break;
      }
      if (scrollProgress > colorStops[colorStops.length - 1].progress) {
        lowerIdx = colorStops.length - 2;
      }
    }

    const stopA = colorStops[lowerIdx];
    const stopB = colorStops[lowerIdx + 1];
    const segmentProgress = (scrollProgress - stopA.progress) / (stopB.progress - stopA.progress);

    // convert colors to RGB arrays
    let colorA: number[];
    let colorB: number[];

    if (typeof stopA.color === "string") {
      // parse hex color
      const hex = stopA.color.replace("#", "");
      colorA = [
        parseInt(hex.substring(0, 2), 16) / 255,
        parseInt(hex.substring(2, 4), 16) / 255,
        parseInt(hex.substring(4, 6), 16) / 255,
      ];
    } else {
      colorA = stopA.color;
    }

    if (typeof stopB.color === "string") {
      // parse hex color
      const hex = stopB.color.replace("#", "");
      colorB = [
        parseInt(hex.substring(0, 2), 16) / 255,
        parseInt(hex.substring(2, 4), 16) / 255,
        parseInt(hex.substring(4, 6), 16) / 255,
      ];
    } else {
      colorB = stopB.color;
    }

    // interpolate between colors
    const r = Math.round((colorA[0] + (colorB[0] - colorA[0]) * segmentProgress) * 255);
    const g = Math.round((colorA[1] + (colorB[1] - colorA[1]) * segmentProgress) * 255);
    const b = Math.round((colorA[2] + (colorB[2] - colorA[2]) * segmentProgress) * 255);

    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: getCurrentColor(),
        zIndex: -1,
        transition: "background-color 0.3s ease-out",
      }}
    />
  );
};
